/**
 * Le parcours « premier jour » : ce qu'il faut avoir créé pour que le produit
 * rende un service visible.
 *
 * Un nouvel administrateur ouvre un menu de vingt entrées et des tableaux
 * vides, et en conclut que c'est encore un logiciel qu'il faut nourrir. Le
 * premier résultat concret — le code à six chiffres qui ouvre l'application du
 * chauffeur — n'arrivait jusqu'ici qu'après la saisie d'un parc entier. Ces
 * quatre créations sont le plus court chemin jusqu'à ce résultat.
 *
 * La définition vit hors de l'écran du parcours parce que la barre latérale et
 * l'accueil doivent savoir s'il reste à faire sans importer l'écran entier.
 */

export const ONBOARDING_ROUTE = '/demarrer';

/**
 * `requires` est une dépendance métier, pas un ordre de préférence : une
 * commande se rattache à un client, donc une licence sans l'écran clients ne
 * peut pas non plus terminer l'étape commande.
 */
export const ONBOARDING_STEPS = [
  {
    id: 'truck',
    module: 'trucks',
    title: 'Votre premier camion',
    description: 'La plaque suffit à l’identifier ; la capacité sert à calculer les volumes.',
  },
  {
    id: 'driver',
    module: 'drivers',
    title: 'Votre premier chauffeur',
    description:
      'Son numéro déclenche la création du code à six chiffres qui ouvre l’application mobile.',
  },
  {
    id: 'client',
    module: 'clients',
    title: 'Votre premier client',
    description: 'Le donneur d’ordre à qui la commande sera facturée.',
  },
  {
    id: 'order',
    module: 'orders',
    requires: 'client',
    title: 'Votre première commande',
    description: 'Le volume demandé, et pour qui.',
  },
];

export const buildOnboardingSteps = (hasModule) => {
  const licensed = ONBOARDING_STEPS.filter((step) => hasModule(step.module));
  const kept = new Set(licensed.map((step) => step.id));
  return licensed.filter((step) => !step.requires || kept.has(step.requires));
};

const STORAGE_KEY = 'ecarbu.onboarding.v1';

// Les seules clés qui repartent sur le disque. Liste explicite plutôt que
// « tout sauf le code d'accès » : un champ ajouté plus tard ne doit pas se
// retrouver persisté par inadvertance.
const CREATED_KEYS = ONBOARDING_STEPS.map((step) => step.id);

const EMPTY_PROGRESS = { done: false, stepIndex: 0, created: {} };

/**
 * Reprendre où l'on s'est arrêté. Sans cela, un rafraîchissement au milieu du
 * parcours renvoie à l'étape 1 et la recréation échoue sur les doublons —
 * plaque déjà connue, téléphone chauffeur déjà pris.
 */
export const readOnboardingProgress = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object') return { ...EMPTY_PROGRESS };
    const created = {};
    CREATED_KEYS.forEach((key) => {
      const value = parsed.created?.[key];
      if (typeof value === 'string' && value) created[key] = value;
    });
    return {
      done: parsed.done === true,
      stepIndex: Number.isInteger(parsed.stepIndex) ? parsed.stepIndex : 0,
      created,
    };
  } catch (_) {
    return { ...EMPTY_PROGRESS };
  }
};

/**
 * Le code d'accès du chauffeur n'entre jamais ici : c'est un identifiant de
 * connexion, il vit le temps de l'écran qui l'affiche et pas dans un stockage
 * que personne ne pense à vider.
 */
export const writeOnboardingProgress = (progress) => {
  const created = {};
  CREATED_KEYS.forEach((key) => {
    const value = progress?.created?.[key];
    if (typeof value === 'string' && value) created[key] = value;
  });
  const safe = {
    done: progress?.done === true,
    stepIndex: Number.isInteger(progress?.stepIndex) ? progress.stepIndex : 0,
    created,
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(safe));
  } catch (_) {
    // Stockage plein ou refusé : le parcours reste jouable, seule la reprise
    // après rafraîchissement se perd.
  }
  return safe;
};

export const markOnboardingDone = () =>
  writeOnboardingProgress({ ...readOnboardingProgress(), done: true });

export const isOnboardingDone = () => readOnboardingProgress().done === true;

export const resetOnboardingProgress = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (_) {
    /* noop */
  }
};
